import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/** Where the viewer parked the launcher, as an offset from its CSS anchor. */
const STORE_KEY = "bl.assistant.launcher.offset";
/** Travel under this many pixels is a press, not a drag — fingers and
 *  trackpads both wobble a little between pointerdown and pointerup, and a
 *  1px twitch must still open the drawer. */
const DRAG_THRESHOLD = 4;
/** Never let the pill be parked flush against (or past) a viewport edge. */
const EDGE_MARGIN = 8;
/** Below half a pixel, a clamp correction isn't worth a render — see the
 *  layout effect that uses it. */
const SETTLE_EPSILON = 0.5;

interface Offset {
  x: number;
  y: number;
}

/** The four edges of a box. A DOMRect satisfies it, and so does a box worked
 *  out arithmetically rather than measured — which is what the clamp below
 *  needs, since it has to reason about where the pill *would* sit. */
interface Bounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const ORIGIN: Offset = { x: 0, y: 0 };

function loadOffset(): Offset {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && Number.isFinite(parsed.x) && Number.isFinite(parsed.y)) {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    /* private mode, or somebody else's junk under our key */
  }
  return ORIGIN;
}

function saveOffset(offset: Offset): void {
  try {
    if (offset.x === 0 && offset.y === 0) localStorage.removeItem(STORE_KEY);
    else localStorage.setItem(STORE_KEY, JSON.stringify(offset));
  } catch {
    /* the pill just won't stay put across reloads */
  }
}

/**
 * The app-wide UI scale from index.css (`--app-zoom`: 0.75, 0.875 or 1 by
 * viewport width).
 *
 * Pointer events report real viewport pixels, but a `transform` length is read
 * in the zoomed root's own units — translating by 100px inside a 0.75 root
 * moves the element 75 real pixels. Every offset therefore has to cross that
 * boundary exactly once, which is what this is for. Same trap that landed the
 * portaled dropdown in components/ui/searchable-select.tsx high and left.
 */
function appZoom(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--app-zoom");
  const zoom = Number.parseFloat(raw);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

/** Clamp a real-pixel move so `rect` ends up fully inside the viewport. */
function clampDelta(rect: Bounds, dx: number, dy: number): Offset {
  const minX = EDGE_MARGIN - rect.left;
  const maxX = window.innerWidth - EDGE_MARGIN - rect.right;
  const minY = EDGE_MARGIN - rect.top;
  const maxY = window.innerHeight - EDGE_MARGIN - rect.bottom;
  // `max` applied last on purpose: when the pill is larger than the viewport
  // the two bounds cross, and pinning its top-left corner beats pinning the
  // bottom-right one off-screen.
  return {
    x: Math.max(minX, Math.min(maxX, dx)),
    y: Math.max(minY, Math.min(maxY, dy)),
  };
}

interface DraggableLauncher<T extends HTMLElement> {
  /** Attach to the element being dragged — the hook measures it to clamp. */
  ref: React.RefObject<T | null>;
  /**
   * Spread onto that same element, alongside its own `onClick`.
   *
   * Only the press is a React prop — the move and release are native listeners
   * on `window`, installed for the life of a gesture. See handleMove below for
   * why that split matters.
   */
  dragProps: {
    style: React.CSSProperties;
    onPointerDown: (e: React.PointerEvent<T>) => void;
    onClickCapture: (e: React.MouseEvent<T>) => void;
    onDragStart: (e: React.DragEvent<T>) => void;
  };
  /** True mid-gesture, for the grabbing cursor and to kill CSS transitions. */
  dragging: boolean;
}

/**
 * Let a `position: fixed` launcher be dragged anywhere in the viewport, and
 * remember where it was left.
 *
 * Niva's pill is anchored bottom-right, which is exactly where the listing
 * filter drawer puts "Apply filters" / "Save this filter" — buyers reported
 * her covering the button with no way past it. Rather than special-case that
 * one drawer (and then the next panel to land in the same corner), the pill
 * moves.
 *
 * Two offsets, not one, and the difference between them is the whole point
 * (TKT-00119). `parked` is the position the viewer chose, and is the only one
 * ever persisted; `offset` is that same position pulled inside whatever
 * viewport exists right now. Folding the clamp back into the stored value —
 * which is what this used to do — ratchets the pill across a phone screen:
 * every scroll shows or hides the browser's URL bar, each of those resizes the
 * layout viewport, and each resize shoved her a little further from where she
 * was put with nothing to move her back when the bar slid away again. Keeping
 * the correction separate means a viewport that shrinks and grows returns her
 * exactly where she was left.
 *
 * The offset is kept in the element's own zoomed units so rendering it stays a
 * plain `translate()` with no scale maths; only the pointer deltas coming in
 * from the browser have to be converted. It survives a reload via
 * localStorage, and is re-clamped (never re-saved) on resize so a shrunk
 * window or a rotated phone can't strand the pill out of reach either.
 */
export function useDraggableLauncher<T extends HTMLElement>(): DraggableLauncher<T> {
  const ref = useRef<T>(null);
  // Where the viewer put her. Only a drag changes this.
  const [parked, setParked] = useState<Offset>(loadOffset);
  // What actually renders: `parked`, corrected inward when the current viewport
  // can't fit her there. Throwaway — never saved, never fed back into `parked`.
  const [offset, setOffset] = useState<Offset>(parked);
  const [dragging, setDragging] = useState(false);
  // Bumped on every viewport change, purely to re-run the clamp effect below.
  const [viewportToken, setViewportToken] = useState(0);
  const gesture = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    /** Real-pixel box at pointerdown — the clamp is relative to this. */
    rect: DOMRect;
    from: Offset;
    zoom: number;
  } | null>(null);
  // A ref, not state: the click guard below reads this in the same tick the
  // gesture ends, and a state update would land a render too late — the drag
  // would open the drawer every time.
  const moved = useRef(false);
  /** Tears down the window listeners a live press installed, if any. */
  const detach = useRef<(() => void) | null>(null);

  /**
   * Move and release are tracked on `window`, and pointer capture is taken at
   * drag START rather than at pointerdown. Both halves of that are load-bearing.
   *
   * Capturing on pointerdown — the obvious shape, and the one that shipped —
   * left the launcher unopenable. Pointer capture retargets the compatibility
   * mouse events too, so the click ending an ordinary press was dispatched at
   * the capturing wrapper, and the <button> nested inside it never received one:
   * `onClick` simply never ran. (Enter on a focused pill still worked, which is
   * why the bug read as "the drawer is broken" rather than "the button is".)
   * Taking capture only once the drag threshold is crossed leaves a plain click
   * alone, while still catching the click that ends a DRAG — that one lands on
   * the wrapper, where onClickCapture swallows it instead of letting it fall
   * through to whatever the pill was dropped on top of.
   *
   * Window listeners then cover what capture used to: a fast flick whose first
   * move already lands outside the pill still starts a drag, and a release
   * anywhere on the page still ends the gesture — including one that happens
   * over another element, which would otherwise leave a gesture armed and drag
   * the pill around on a later hover (the mouse keeps one pointerId forever).
   */
  const handleMove = useCallback((e: PointerEvent) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (!moved.current) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      moved.current = true;
      setDragging(true);
      ref.current?.setPointerCapture(e.pointerId);
    }
    const d = clampDelta(g.rect, dx, dy);
    const next = { x: g.from.x + d.x / g.zoom, y: g.from.y + d.y / g.zoom };
    // Both, because this is a deliberate placement: it becomes the new intent.
    setOffset(next);
    setParked(next);
  }, []);

  const handleUp = useCallback((e: PointerEvent) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    gesture.current = null;
    detach.current?.();
    detach.current = null;
    setDragging(false);
    // `moved` deliberately stays set: the click that follows this release has
    // to see it. onPointerDown resets it for the next press.
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<T>) => {
      const el = ref.current;
      // Primary button only; a right-click belongs to the context menu.
      if (!el || e.button !== 0) return;
      gesture.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        rect: el.getBoundingClientRect(),
        // `offset`, not `parked`: a drag continues from where she is drawn.
        from: offset,
        zoom: appZoom(),
      };
      moved.current = false;
      // A press that somehow never saw its release must not leave a second set
      // of listeners behind.
      detach.current?.();
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointercancel", handleUp);
      detach.current = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleUp);
      };
    },
    [offset, handleMove, handleUp],
  );

  // Unmounting mid-gesture (sign-out, the drawer opening under the press) must
  // not leave the window listening for a pointer nobody is tracking any more.
  useEffect(() => () => {
    detach.current?.();
    detach.current = null;
  }, []);

  const onClickCapture = useCallback((e: React.MouseEvent<T>) => {
    if (!moved.current) return;
    // The release at the end of a drag also fires a click. Swallow it before
    // the button's own handler runs, or repositioning always opens the drawer.
    e.preventDefault();
    e.stopPropagation();
    moved.current = false;
  }, []);

  // Native HTML drag-and-drop, started from the avatar <img> inside, would
  // otherwise fight the pointer gesture and leave a ghost image trailing.
  const onDragStart = useCallback((e: React.DragEvent<T>) => e.preventDefault(), []);

  // Writing on every pointermove would hammer localStorage 60 times a second
  // for no benefit, so persist once the gesture settles. The mount pass
  // re-writes what it just read, which is both harmless and how a stored value
  // that has drifted out of shape gets normalised.
  useEffect(() => {
    if (dragging) return;
    saveOffset(parked);
  }, [dragging, parked]);

  // Keep the rendered offset as close to `parked` as this viewport allows.
  //
  // The target box is derived arithmetically from the live one rather than
  // rendered and measured: moving her to `parked`, measuring, then correcting
  // would paint the uncorrected frame first and flicker. `offset` is a
  // dependency on purpose — applying a correction re-runs this, which
  // recomputes the same target from the new rect, agrees with itself and
  // stops. A layout effect so the whole settle happens before paint.
  useLayoutEffect(() => {
    if (dragging) return;
    const el = ref.current;
    if (!el) return;
    const zoom = appZoom();
    const rect = el.getBoundingClientRect();
    const shiftX = (parked.x - offset.x) * zoom;
    const shiftY = (parked.y - offset.y) * zoom;
    const target: Bounds = {
      left: rect.left + shiftX,
      right: rect.right + shiftX,
      top: rect.top + shiftY,
      bottom: rect.bottom + shiftY,
    };
    const d = clampDelta(target, 0, 0);
    const next = { x: parked.x + d.x / zoom, y: parked.y + d.y / zoom };
    // Tolerance, not equality. Reconstructing the target from a measured rect
    // reintroduces however the engine quantised the last transform, so two
    // passes can disagree in the twelfth decimal — and an exact test would
    // then hand this effect a correction to apply forever.
    if (Math.abs(next.x - offset.x) > SETTLE_EPSILON || Math.abs(next.y - offset.y) > SETTLE_EPSILON) {
      setOffset(next);
    }
  }, [parked, offset, dragging, viewportToken]);

  useEffect(() => {
    const onViewportChange = () => setViewportToken((t) => t + 1);
    window.addEventListener("resize", onViewportChange);
    // Not redundant on phones: a rotation can settle its final size after the
    // resize that announced it, and the clamp has to see the settled one.
    window.addEventListener("orientationchange", onViewportChange);
    return () => {
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("orientationchange", onViewportChange);
    };
  }, []);

  return {
    ref,
    dragProps: {
      style: {
        transform: offset.x || offset.y ? `translate(${offset.x}px, ${offset.y}px)` : undefined,
        // Without this a touch drag scrolls the page instead of moving the pill.
        touchAction: "none",
      },
      onPointerDown,
      onClickCapture,
      onDragStart,
    },
    dragging,
  };
}

export default useDraggableLauncher;
